import React, { useEffect, useState } from 'react';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import { 
  InformationCircleIcon,
  PlayCircleIcon,
  AcademicCapIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { getResourceBySlug } from '../../../lib/db';

const ResettingBehavioursKeyInfo = () => {
  const [resource, setResource] = useState(null);
  const metrics = [
    {
      type: 'difficulty',
      title: 'Difficulty',
      value: 'High'
    },
    {
      type: 'timeline',
      title: 'Timeline',
      value: '2 Hours - several weeks'
    },
    {
      type: 'size',
      title: 'Team Size',
      value: 'All'
    },
    {
      type: 'why',
      title: 'Why Use',
      value: 'Documenting one thing and doing another is a surefire way to create mistrust. Trust is the foundation of high performing teams and without them strategy execution is a pipe dream.'
    },
    {
      type: 'when',
      title: 'When to Use',
      value: 'If you see consistent anti-behaviour that isn\'t isolated to a particular individual (i.e. it\'s widespread) and you want to do something about it.'
    }
  ];

  useEffect(() => {
    getResourceBySlug('resetting-behaviours').then(setResource);
  }, []);
  
  return (
    <div>
      <PlaybookHero
        title={resource?.title || "How To Reset Misaligned Behaviour"}
        subtitle={resource?.description || "A step-by-step guide to resetting behaviours and addressing anti-values in your organization"}
        category={resource?.category || "Culture"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "15 min read"}
        type={resource?.type}
      />
      <div id="key-info" className="prose prose-invert max-w-none">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <InformationCircleIcon className="h-6 w-6 text-primary-light mr-2" />
          Key Info
        </h2>
        {/* This Playbook is for you section */}
        <h3 className="text-xl font-semibold mb-4">This Guide is for you if either of the following are true:</h3>
        <ul className="space-y-3 mb-8">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You have a set of core values and associated behaviours that you spent some time working up but they aren't really operationalised–you can't see them in practice.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>When you observe how teams and team members are working together…you are seeing more and more anti-behaviours and anti-values.</span>
          </li>
        </ul>
        {/* Guide Overview */}
        <h3 className="text-xl font-semibold mb-4">Guide Overview</h3>
        <div className="mb-12">
          <PlaybookMetrics metrics={metrics} />
        </div>
        {/* Watch This section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <PlayCircleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Watch This</h3>
          </div>
          <VideoEmbed src="https://www.loom.com/embed/46f1240e22af46e898d3b4279aedc935" title="Resetting Behaviours Guide" />
        </div>
        {/* What Is This Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <AcademicCapIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What Is This</h3>
          </div>
          <div className="space-y-4">
            <p className="text-lg">
              So this isn't about individual behaviour. It's not a performance conversation. This playbook is for when you notice wide-spread anti-behaviours and anti-values.
            </p>
            <p className="text-lg">
              Perhaps you value collaboration or 'less me, more we' but when you look around you see the opposite being embodied.
            </p>
            <p className="text-lg">
              It's very easy, especially if you've just been through something big as a business (change, merger, acquisition, hyper growth etc…), to slip into ways of working that don't serve you.
            </p>
            <div className="flex items-center font-semibold mt-4 text-lg">
              Welcome to your step-by-step guide to resetting behaviours and addressing this problem. Let's get into it <span className="ml-2 text-xl"></span>
            </div>
          </div>
        </div>
        {/* What You'll Need Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <WrenchScrewdriverIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What You'll Need</h3>
          </div>
          <ul className="space-y-3 pl-5 list-disc">
            <li className="text-gray-300">Documented and published core values/behaviours or operating principles.</li>
            <li className="text-gray-300">Clear examples of where you've observed anti-behaviours</li>
            <li className="text-gray-300">Clarity on how anti-behaviours are negatively impacting your business and the outcomes you are trying to achieve</li>
            <li className="text-gray-300">Support from the leadership team that this is worth solving.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResettingBehavioursKeyInfo; 